import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import { DialogBody, DialogFooter } from '../../../../web/components-v2/Dialog';
import { Input } from '../../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../web/components-v2/Select';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { useCategories } from '../../../queries/Scenarios';
import type { CreateSkillAreaInput, SkillArea, UpdateSkillAreaInput } from '../../../voice-agent/types/scenarios';
import { styleSheet } from './styles';

interface IProps {
	skillArea?: SkillArea | null;
	/** Default category ID for creating a new skill area under a specific category */
	defaultCategoryId?: string | null;
	onSubmit: (input: CreateSkillAreaInput | UpdateSkillAreaInput) => Promise<void>;
	onCancel: () => void;
	isSubmitting: boolean;
}

export const SkillAreaForm: React.FC<IProps> = ({ skillArea, defaultCategoryId, onSubmit, onCancel, isSubmitting }) => {
	const [name, setName] = React.useState(skillArea?.name || '');
	const [description, setDescription] = React.useState(skillArea?.description || '');
	const [categoryId, setCategoryId] = React.useState(skillArea?.categoryId || defaultCategoryId || '');
	const [active, setActive] = React.useState(skillArea?.active ?? true);
	const [errors, setErrors] = React.useState<{ name?: string; categoryId?: string }>({});

	const { data: categoriesData, isPending: isLoadingCategories } = useCategories();
	// Hooks now return arrays directly
	const categories = React.useMemo(() => categoriesData || [], [categoriesData]);

	const selectedCategory = categories.find(cat => cat.id === categoryId);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Validate required fields
		const newErrors: { name?: string; categoryId?: string } = {};

		if (!name.trim()) {
			newErrors.name = 'Name is required';
		}
		if (!categoryId) {
			newErrors.categoryId = 'Category is required';
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		setErrors({}); // Clear errors on valid submit

		const input: CreateSkillAreaInput | UpdateSkillAreaInput = {
			name: name.trim(),
			description: description.trim(),
			categoryId,
			active,
		};

		await onSubmit(input);
	};

	const handleCategoryChange = (value: string) => {
		setCategoryId(value);
		if (errors.categoryId) {
			setErrors(prev => ({ ...prev, categoryId: undefined }));
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<DialogBody>
				<div className={css(styleSheet.formGroup)}>
					<label htmlFor='name' className={css(styleSheet.label)}>
						Name <span className={css(styleSheet.required)}>*</span>
					</label>
					<Input
						type='text'
						id='name'
						value={name}
						onChange={e => {
							setName(e.target.value);
							if (errors.name) {
								setErrors(prev => ({ ...prev, name: undefined }));
							}
						}}
						placeholder='Enter skill area name'
						required
						disabled={isSubmitting}
					/>
					{errors.name && <span className={css(styleSheet.error)}>{errors.name}</span>}
				</div>

				<div className={css(styleSheet.formGroup)}>
					<label htmlFor='description' className={css(styleSheet.label)}>
						Description
					</label>
					<TextArea
						id='description'
						value={description}
						onChange={e => setDescription(e.target.value)}
						placeholder='Enter skill area description (optional)'
						rows={3}
						disabled={isSubmitting}
					/>
				</div>

				<div className={css(styleSheet.formGroup)}>
					<label htmlFor='category' className={css(styleSheet.label)}>
						Category <span className={css(styleSheet.required)}>*</span>
					</label>
					{isLoadingCategories ? (
						<div style={{ padding: '8px 0' }}>
							<LoadingSpinner />
						</div>
					) : (
						<Select value={categoryId} onValueChange={handleCategoryChange} disabled={isSubmitting}>
							<SelectTrigger>
								{selectedCategory ? `${selectedCategory.name} (${selectedCategory.role})` : 'Select a category'}
							</SelectTrigger>
							<SelectContent>
								{categories.map(cat => (
									<SelectItem key={cat.id} value={cat.id}>
										{cat.name} ({cat.role})
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
					{errors.categoryId && <span className={css(styleSheet.error)}>{errors.categoryId}</span>}
				</div>

				<div className={css(styleSheet.formGroup)}>
					<label className={css(styleSheet.checkboxLabel)}>
						<Checkbox
							checked={active}
							onCheckedChange={checked => setActive(checked === true)}
							disabled={isSubmitting}
						/>
						<span className={css(styleSheet.checkboxText)}>Active</span>
					</label>
				</div>
			</DialogBody>

			<DialogFooter>
				<Button type='button' variant='secondary' onClick={onCancel} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button type='submit' variant='primary' disabled={isSubmitting || !name.trim() || !categoryId}>
					{isSubmitting ? 'Saving...' : skillArea ? 'Update' : 'Create'}
				</Button>
			</DialogFooter>
		</form>
	);
};
